import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LegalLayout } from './LegalLayout';

interface Entry {
  date: string;
  version: string;
  kind: 'feature' | 'fix' | 'security' | 'note';
  title: string;
  body: string;
}

const ENTRIES: Entry[] = [
  {
    date: '2026-05-23',
    version: 'v1.0',
    kind: 'feature',
    title: 'Knowlez v1 launches.',
    body:
      'Email + password + Google + Apple OAuth across 5 auth pages and 11 controller endpoints. Hybrid retrieval (vector + lexical + knowledge graph) with Reciprocal Rank Fusion. 6 AI agents (Search, Compose, Brief, Compare, Audit, Workflow). 14-provider connector catalog with real sync for Google Drive, M365, Slack, Notion. SSO/SAML, OIDC, SCIM v2, audit log. 14 languages with RTL for ar + ur. Cloud or on-prem from the same codebase.',
  },
];

const KIND_STYLES: Record<Entry['kind'], { label: string; cls: string }> = {
  feature: { label: 'Feature', cls: 'bg-accent-50 text-accent-700 border-accent-200' },
  fix: { label: 'Fix', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  security: { label: 'Security', cls: 'bg-warn-500/10 text-warn-600 border-warn-500/30' },
  note: { label: 'Note', cls: 'bg-ink-100 text-ink-600 border-ink-200' },
};

export function ChangelogPage() {
  const { t } = useTranslation();
  const latest = ENTRIES[0]?.date ?? new Date().toISOString().slice(0, 10);
  return (
    <LegalLayout
      eyebrow={t('changelog.eyebrow', { defaultValue: 'Changelog' }) as string}
      title={t('changelog.title', { defaultValue: 'What changed.' })}
      updated={latest}
    >
      <p className="text-[14.5px] text-ink-700 leading-relaxed mb-8">
        {t('changelog.intro', {
          defaultValue:
            'Knowlez ships continuously. Material changes are recorded here; security fixes are also surfaced in the in-product notification bell. Our open-source components and licenses are listed on the ',
        })}
        <Link to="/oss" className="text-accent-600 hover:underline">{t('changelog.oss_link', { defaultValue: 'open-source page' })}</Link>.
      </p>

      <ol className="space-y-8 relative">
        {ENTRIES.map((e) => {
          const style = KIND_STYLES[e.kind];
          return (
            <li key={`${e.date}-${e.version}`} className="relative pl-6 border-l-2 border-ink-100">
              <span className="absolute -left-[5px] top-1 h-2 w-2 rounded-full bg-accent-500" />
              <div className="flex flex-wrap items-center gap-2 text-[11px]">
                <span className="num font-mono text-ink-500">{e.date}</span>
                <span className="text-ink-300">·</span>
                <span className="num font-mono text-ink-700 font-semibold">{e.version}</span>
                <span
                  className={`inline-flex items-center text-[10px] uppercase tracking-[0.14em] font-semibold rounded px-2 py-0.5 border ml-1 ${style.cls}`}
                >
                  {style.label}
                </span>
              </div>
              <h3 className="mt-2 display text-[18px] font-semibold text-ink-900 leading-snug">
                {e.title}
              </h3>
              <p className="mt-2 text-[14px] text-ink-700 leading-relaxed">{e.body}</p>
            </li>
          );
        })}
      </ol>
    </LegalLayout>
  );
}
